import React from "react";
import { Link } from "react-router-dom";

/**
 * StatCard — uniform KPI cell, brutalist sharp borders, optional yellow accent.
 * Compatible with the existing dashboard "Kpi" usage but extracted as DS.
 *
 *  - icon: lucide-react component
 *  - label: short uppercase label
 *  - value: number or string
 *  - accent: highlight with the brand yellow
 *  - tone: "default" | "danger" | "warning" | "success" | "navy" | "yellow"
 *  - to: optional internal link (renders as <Link>)
 *  - href: optional external link
 *  - hint: small muted text below value
 *  - testId
 */
const TONE_CLASS = {
  default: "bg-white text-zinc-900",
  yellow: "bg-[#FFC60A] text-zinc-900",
  navy: "bg-zinc-900 text-white",
  success: "bg-emerald-50 text-emerald-900",
  warning: "bg-orange-50 text-orange-900",
  danger: "bg-red-50 text-red-900",
};

export function StatCard({ icon: Icon, label, value, accent = false, tone, to, href, hint, testId, onClick }) {
  const resolvedTone = tone || (accent ? "yellow" : "default");
  const isInteractive = !!(to || href || onClick);
  const cls = [
    "border border-zinc-900 p-5 transition-all flex flex-col justify-between min-h-[110px]",
    TONE_CLASS[resolvedTone],
    isInteractive ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-[6px_6px_0_0_#18181b]" : "",
  ].join(" ");

  const inner = (
    <>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-mono tracking-[0.25em] uppercase opacity-70 truncate">{label}</span>
        {Icon && <Icon size={16} className="shrink-0" />}
      </div>
      <div className="mt-2">
        <div className="font-display font-black text-3xl lg:text-4xl tracking-tighter leading-none">{value}</div>
        {hint && <div className="mt-1 text-[10px] font-mono uppercase tracking-widest opacity-70">{hint}</div>}
      </div>
    </>
  );

  if (to) return <Link to={to} data-testid={testId} className={cls}>{inner}</Link>;
  if (href) return <a href={href} data-testid={testId} className={cls}>{inner}</a>;
  if (onClick) return <button type="button" onClick={onClick} data-testid={testId} className={`${cls} text-left w-full`}>{inner}</button>;
  return <div data-testid={testId} className={cls}>{inner}</div>;
}

/**
 * StatGrid — wrapper that builds the seamless KPI strip (collapsed borders).
 *  - cols: number of cols on desktop (default 4)
 *  - children: <StatCard> ...
 */
export function StatGrid({ children, cols = 4, className = "", testId }) {
  const colMap = { 2: "lg:grid-cols-2", 3: "lg:grid-cols-3", 4: "lg:grid-cols-4", 5: "lg:grid-cols-5", 6: "lg:grid-cols-6" };
  // Outer container holds the unique 1px border; children rendered borderless
  // via divide-x/y so the grid looks like a single sharp matrix.
  return (
    <div
      data-testid={testId}
      className={`grid grid-cols-2 sm:grid-cols-3 ${colMap[cols] || "lg:grid-cols-4"} gap-0 border border-zinc-900 divide-x divide-y divide-zinc-900 [&>*]:!border-0 ${className}`}
    >
      {children}
    </div>
  );
}
